import { useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ensureLanguageResources, supportedLanguages, type SupportedLanguage } from "@/i18n/config";

const LANG_STORAGE_KEY = "ekokompakt-lang";

export function useLanguage() {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const currentLang = (lang && supportedLanguages.includes(lang as SupportedLanguage) ? lang : "cs") as SupportedLanguage;

  useEffect(() => {
    let cancelled = false;

    const syncLanguage = async () => {
      try {
        await ensureLanguageResources(currentLang);
      } catch (error) {
        console.warn(`Failed to ensure language resources for ${currentLang}`, error);
      }

      if (!cancelled && i18n.language !== currentLang) {
        await i18n.changeLanguage(currentLang);
      }

      if (!cancelled) {
        document.documentElement.lang = currentLang;
      }
    };

    void syncLanguage();

    return () => {
      cancelled = true;
    };
  }, [currentLang, i18n]);

  const changeLanguage = (newLang: SupportedLanguage) => {
    localStorage.setItem(LANG_STORAGE_KEY, newLang);
    const pathWithoutLang = location.pathname.replace(/^\/(cs|en|de|sk|hr|it|nl|et|lt|lv|hu)/, "") || "/";
    const newPath = newLang === "cs" ? pathWithoutLang : `/${newLang}${pathWithoutLang === "/" ? "" : pathWithoutLang}`;
    navigate(newPath + location.search + location.hash);
  };

  const localePath = (path: string) => {
    if (currentLang === "cs") return path;
    return `/${currentLang}${path === "/" ? "" : path}`;
  };

  return { currentLang, changeLanguage, localePath };
}

export function getPreferredLanguage(): SupportedLanguage {
  // Check localStorage first (user manual choice)
  const stored = localStorage.getItem(LANG_STORAGE_KEY);
  if (stored && supportedLanguages.includes(stored as SupportedLanguage)) {
    return stored as SupportedLanguage;
  }
  // Then browser language
  const browserLang = navigator.language?.slice(0, 2).toLowerCase();
  if (supportedLanguages.includes(browserLang as SupportedLanguage)) {
    return browserLang as SupportedLanguage;
  }
  return "cs";
}
